/**
 * SessionStorage Backend
 *
 * Session-scoped storage that persists only for the duration of the page session.
 * Data is cleared when the tab/window is closed.
 * Falls back to memory storage if sessionStorage is unavailable.
 */

import { MemoryBackend } from './memory';
import type { StorageBackend, StorageBackendOptions } from './types';

export class SessionStorageBackend implements StorageBackend {
  private fallback: MemoryBackend | null = null;

  get(key: string): string | null {
    if (!this.isSupported()) {
      return this.getFallback().get(key);
    }

    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.warn('sessionStorage.getItem failed:', error);
      return this.getFallback().get(key);
    }
  }

  set(key: string, value: string, _options?: StorageBackendOptions): void {
    if (!this.isSupported()) {
      this.getFallback().set(key, value);
      return;
    }

    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.warn('sessionStorage.setItem failed, falling back to memory:', error);
      this.getFallback().set(key, value);
    }
  }

  remove(key: string): void {
    if (!this.isSupported()) {
      this.getFallback().remove(key);
      return;
    }

    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('sessionStorage.removeItem failed:', error);
      this.getFallback().remove(key);
    }
  }

  clear(): void {
    if (!this.isSupported()) {
      this.getFallback().clear();
      return;
    }

    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn('sessionStorage.clear failed:', error);
      this.getFallback().clear();
    }
  }

  isSupported(): boolean {
    try {
      const testKey = '__storage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private getFallback(): MemoryBackend {
    if (!this.fallback) {
      this.fallback = new MemoryBackend();
    }
    return this.fallback;
  }
}
